/**
 * Hero banner: poster first (LCP), wideo lazy po idle/load.
 * Safari: muted + playsInline + play(); przy blokadzie / LPM — poster.
 */
const VIDEO_SELECTOR = 'section.banner video[data-banner-hero-video]';
const LOAD_DELAY_MS = 400;
const IDLE_TIMEOUT_MS = 2500;

function shouldSkipHeroVideo() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return true;
    }

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) {
        return false;
    }

    if (connection.saveData) {
        return true;
    }

    return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
}

function markVideoSkipped(video) {
    video.setAttribute('data-banner-hero-video-skipped', '');
    video.classList.add('is-autoplay-blocked');
}

function hasVideoSource(video) {
    return video.dataset.bannerHeroVideoLoaded === '1' || video.querySelector('source') !== null;
}

function attachVideoSource(video) {
    if (!(video instanceof HTMLVideoElement) || hasVideoSource(video)) {
        return;
    }

    const src = video.dataset.videoSrc;
    if (!src) {
        return;
    }

    const source = document.createElement('source');
    source.src = src;
    source.type = video.dataset.videoType || 'video/mp4';
    video.appendChild(source);
    video.dataset.bannerHeroVideoLoaded = '1';
    video.load();
}

function kickBannerVideo(video) {
    if (!(video instanceof HTMLVideoElement)) {
        return;
    }

    if (video.hasAttribute('data-banner-hero-video-skipped')) {
        return;
    }

    if (!hasVideoSource(video)) {
        attachVideoSource(video);
    }

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.controls = false;
    video.removeAttribute('controls');

    const attempt = () => {
        if (document.visibilityState === 'hidden') {
            return;
        }

        const playPromise = video.play();
        if (playPromise === undefined) {
            return;
        }

        playPromise
            .then(() => {
                video.classList.remove('is-autoplay-blocked');
                video.classList.add('is-banner-hero-video-ready');
                video.removeAttribute('data-banner-hero-video-blocked');
            })
            .catch(() => {
                video.pause();
                video.classList.remove('is-banner-hero-video-ready');
                video.classList.add('is-autoplay-blocked');
                video.setAttribute('data-banner-hero-video-blocked', '');
            });
    };

    attempt();

    if (video.readyState < 2) {
        video.addEventListener('loadeddata', attempt, { once: true });
        video.addEventListener('canplay', attempt, { once: true });
    }
}

function observeBannerVideos() {
    if (!('IntersectionObserver' in window)) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const video = entry.target;
                if (!(video instanceof HTMLVideoElement)) {
                    return;
                }
                if (entry.isIntersecting) {
                    kickBannerVideo(video);
                } else {
                    video.pause();
                }
            });
        },
        { threshold: 0.12 }
    );

    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => observer.observe(video));
}

function scheduleHeroVideoLoads() {
    const videos = document.querySelectorAll(VIDEO_SELECTOR);
    if (!videos.length) {
        return;
    }

    const start = () => {
        videos.forEach((video) => {
            if (shouldSkipHeroVideo()) {
                markVideoSkipped(video);
                return;
            }
            attachVideoSource(video);
            kickBannerVideo(video);
        });
    };

    const runDeferred = () => {
        window.setTimeout(start, LOAD_DELAY_MS);
    };

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(runDeferred, { timeout: IDLE_TIMEOUT_MS });
    } else if (document.readyState === 'complete') {
        runDeferred();
    } else {
        window.addEventListener('load', runDeferred, { once: true });
    }
}

export function initBannerHeroBackgroundVideo() {
    observeBannerVideos();
    scheduleHeroVideoLoads();

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') {
            return;
        }
        document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
            if (!video.hasAttribute('data-banner-hero-video-skipped')) {
                kickBannerVideo(video);
            }
        });
    });

    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) {
            return;
        }
        document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
            if (!video.hasAttribute('data-banner-hero-video-skipped')) {
                kickBannerVideo(video);
            }
        });
    });
}
